import { VenueConfig, VenuePage } from "../../types/venues";
import { VenueUserConfig } from "../../types/VenueUser";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { dmnStorage } from "../../utils/dmnStorage";
import { getEnvironmentProps } from "../../utils/environment";
import { getPermittedVenues } from "../../utils/venues/getPermittedVenues";
import { errorHandler, throwApiError } from "../errorHandler";
import { collinsApiHeaders } from "../getDefaultHeaders";
import { readJsonBody } from "../responseBody";

type getVenueConfigType = {
  venues: VenuePage[] | undefined;
  venueUserConfig?: VenueUserConfig | null;
};
/**
 * Selects the only selectable venue, the last-opened selectable venue, or the first.
 */
export const getDefaultVenue = async ({
  venues,
  venueUserConfig
}: getVenueConfigType): Promise<string | undefined> => {
  if (!venues?.[0]?._id) {
    return;
  }

  const permittedVenues = venueUserConfig
    ? getPermittedVenues(venues, venueUserConfig)
    : venues.filter((venue) => !!venue.title);
  const selectableVenues =
    permittedVenues.length > 0 ? permittedVenues : venues;

  if (selectableVenues.length === 1) {
    return selectableVenues[0]._id;
  }

  const storedVenueId = await dmnStorage.get("venueId");
  return (
    selectableVenues.find((venue) => venue._id === storedVenueId)?._id ??
    selectableVenues[0]._id
  );
};

export const fetchVenueConfig = async (
  id: string | undefined
): Promise<VenueConfig | undefined> => {
  if (!id) {
    return undefined;
  }
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  try {
    const response = await fetch(
      `${environmentVars?.CORE_API_BASE_URL}/venues/${id}`,
      {
        method: "GET",
        headers: collinsApiHeaders(token)
      }
    );

    if (!response.ok) {
      await throwApiError(response, "Error fetching venue config");
    }

    const data = await readJsonBody<{ payload?: { venue?: VenueConfig } }>(
      response
    );

    if (data === undefined) {
      return undefined;
    }

    const venueConfig = data.payload?.venue;

    if (!venueConfig) {
      await errorHandler(
        new Error("Venue config not found"),
        "Error fetching venue config"
      );
      return undefined;
    }

    return venueConfig;
  } catch (error) {
    await errorHandler(error, "Error fetching venue config");
    return undefined;
  }
};
